
import { useDebounce } from '@uidotdev/usehooks'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslations } from '@/hooks/useTranslations'
import { DateRangePicker, type DateRange } from '@/components/common/DatePicker/DateRangePicker'
import dayjs from '@/lib/dayjs'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Skeleton as BoneyardSkeleton } from 'boneyard-js/react'
import { Loader } from '@/components/common/Loader'
// import { useConfirmDeleteUserModal, useConfirmInviteModal, useInvitedWarningModal } from '@/components/common/Modals'
import { toast } from '@/components/common/Toast'
import { Button, LinkButton, SelectContent, Select, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { UserDropdown, UserOrganizationStatus, UserStatus } from '@/constants/user'
// import {
//   useDeleteUserOrganization,
//   useGetOrganizationUserList,
//   useResendInviteUserOrganization,
// } from '@/modules/user/services/user'

import { useDatatableHeaders } from './columns'
import { ExportCsvDialog, type PdfMode } from './ExportCsvDialog'
import {
  buildExportCsvContent,
  downloadCsvFile,
  exportFilenameForTemplate,
  fetchAllBillsForExport,
  fetchExportCatalog,
  filterRowsForExportTemplate,
  hasExportableRows,
  saleModeForExportTemplate,
  validateExportTemplate,
  type BillExportTemplate,
} from './exportBillsCsv'
import { useDataTable } from '@/hooks/useDataTable'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@/constants/datatable'
import { DataTable } from '@/components/common/DataTable/DataTable'
import { Profile, useCreateUser, useDeleteUser, useGetUserList, useUpdateUser } from '@/modules/user/services/user'
import { useDeleteBill, useGetBillList, useGetBillTotal, exportBillsYearPdf } from '@/modules/bill/services/bill'
import { TabsSelect } from '@/components/common/TabsSelect'
import { Bill } from '@/models/bill/bill'
import { formatNumber } from '@/utils/number'
import { useGetCustomerNoPage } from '@/modules/customer/services/customer'
import { BillCustomerTextCombobox } from '@/components/pages/Admin/BillManagement/Form/BillCustomerTextCombobox'
import { useConfirmSaveModal } from '@/components/common/Modals/ConfirmSaveModal'
import { useProfileData } from '@/hooks/useProfile'
import { Skeleton as UiSkeleton } from '@/components/ui/skeleton'


import { useInitialLocationFilter } from '@/hooks/useInitialLocationFilter'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from '@/constants/bill-location'

const BKK = 'Asia/Bangkok'

function ymdToDate(ymd: string): Date {
  return dayjs.tz(ymd, 'YYYY-MM-DD', BKK).toDate()
}

function dateToYmd(d: Date): string {
  return dayjs(d).tz(BKK).format('YYYY-MM-DD')
}

type BillManagementListProps = {
  type?: 'all' | 'pending'
}

export const BillManagementList = ({ type }: BillManagementListProps) => {
  const t = useTranslations('admin.bill_management')
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { tab?: string; q?: string }
  const [searchTerm, setSearchTerm] = useState(type === 'pending' ? (searchParams.q || '') : '')
  const [status, setStatus] = useState<string>('')
  const [tab, setTab] = useState<string>(searchParams.tab || 'buy')
  const [billDateFrom, setBillDateFrom] = useState<string>('')
  const [billDateTo, setBillDateTo] = useState<string>('')
  const [locationFilter, setLocationFilter] = useInitialLocationFilter()
  const [paymentMethod, setPaymentMethod] = useState<string>('all')
  const [paymentStatus, setPaymentStatus] = useState<string>('all')
  const [printStatus, setPrintStatus] = useState<string>('all')
  const [saleModeFilter, setSaleModeFilter] = useState<'all' | 'retail' | 'wholesale'>('all')
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const [searchTerms, setSearchTerms] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)
  const [billToDelete, setBillToDelete] = useState<Bill | null>(null)

  const { ConfirmSaveModal: ConfirmDeleteModal, showConfirmSaveModal: showConfirmDeleteModal } = useConfirmSaveModal()

  const billDateRangeValue = useMemo((): DateRange | undefined => {
    if (!billDateFrom && !billDateTo) {
      return undefined
    }
    return {
      from: billDateFrom ? ymdToDate(billDateFrom) : undefined,
      to: billDateTo ? ymdToDate(billDateTo) : undefined,
    }
  }, [billDateFrom, billDateTo])

  const onBillDateRangeChange = (r: DateRange | undefined) => {
    if (!r?.from && !r?.to) {
      setBillDateFrom('')
      setBillDateTo('')
      return
    }
    setBillDateFrom(r.from ? dateToYmd(r.from) : '')
    setBillDateTo(r.to ? dateToYmd(r.to) : '')
  }

  const $apiDeleteBill = useDeleteBill()

  const onClickBillDropdownEvent = useCallback(
    async (eventName: string, item: Bill) => {
      switch (eventName) {
        case UserDropdown.VIEW:
          navigate({
            to: type === "pending" ? `/admin/outstanding/${item.id}` : `/admin/bill/${item.id}`,
            search: type === "pending" ? { tab, q: searchTerm || undefined } : undefined,
          })
          break
        case UserDropdown.EDIT:
          navigate({ 
            to: type === "pending" ? `/admin/outstanding/${item.id}/edit` : `/admin/bill/${item.id}/edit`,
            search: type === "pending" ? { tab, q: searchTerm || undefined } : { tab }
          })
          break
        case UserDropdown.DELETE:
          setBillToDelete(item)
          showConfirmDeleteModal()
          break
      }
    },
    [navigate, searchTerm, tab, type],
  )

  const { data: customerListNoPage } = useGetCustomerNoPage()

  const { columns } = useDatatableHeaders({ search: searchTerms, onClickBillDropdownEvent })

  const { dataTableProps, state, setData, setTotalPages, setPagination } = useDataTable({
    columns,
    showPageSize: true,
    showPagination: true,
    isLoading: false,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    sorting: [
      {
        id: 'created_at',
        desc: true,
      },
    ],
  })

  const { data, isPending } = useGetBillList({
    page: state.pagination.page,
    pageSize: state.pagination.pageSize,
    search: searchTerms,
    status: tab,
    sort: state.sorting,
    bill_date_from: billDateFrom || undefined,
    bill_date_to: billDateTo || undefined,
    location: locationFilter || undefined,
    payment_method: paymentMethod === "all" ? undefined : paymentMethod,
    payment_status:
      type === 'pending' ? undefined : paymentStatus === 'all' ? undefined : paymentStatus,
    outstanding: type === 'pending',
    print_status: printStatus === "all" ? undefined : printStatus,
    sale_mode: tab === 'sell' && saleModeFilter !== 'all' ? saleModeFilter : undefined,
    enabled: true,
  })

  const { data: totalReport, isPending: isPendingTotalReport } = useGetBillTotal({
    page: state.pagination.page,
    pageSize: state.pagination.pageSize,
    search: searchTerms,
    status: tab,
    sort: state.sorting,
    bill_date_from: billDateFrom || undefined,
    bill_date_to: billDateTo || undefined,
    location: locationFilter || undefined,
    payment_method: paymentMethod === "all" ? undefined : paymentMethod,
    payment_status:
      type === 'pending' ? undefined : paymentStatus === 'all' ? undefined : paymentStatus,
    outstanding: type === 'pending',
    print_status: printStatus === "all" ? undefined : printStatus,
    sale_mode: tab === 'sell' && saleModeFilter !== 'all' ? saleModeFilter : undefined,
    enabled: true,
  })


  useEffect(() => {
    setSearchTerms(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  useEffect(() => {

    setData(data?.data ?? [])
    setTotalPages(data?.meta?.totalPages ?? 1)
  }, [data, setData, setTotalPages])

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: state.pagination.pageSize })
  }, [searchTerm, status, billDateFrom, billDateTo, locationFilter, tab, saleModeFilter])

  // const handleClickResendInvitation = async (user: any | null) => {
  //   try {
  //     setIsLoading(true)
  //     if (!user) {
  //       throw new Error('User is not found')
  //     }

  //     await $apiResendInvite.submitResend({
  //       orgId,
  //       userId: user.id,
  //     })

  //     toast.toastSuccess(<HighlightWord message={tForm('toast.resend_invitation_success', { email: user.email })} />)
  //   } catch (error: any) {
  //     console.error(error)
  //     toast.toastFailed(tForm('toast.resend_invitation_failed'))
  //   } finally {
  //     setIsLoading(false)
  //     setUserSelected(null)
  //   }
  // }

  // const handleClickDeleteUser = async (user: any | null) => {
  //   try {
  //     setIsLoading(true)
  //     if (!user) {
  //       throw new Error('User is not found')
  //     }

  //     await $apiDeleteUser.submitDelete({
  //       orgId,
  //       userId: user.id,
  //     })

  //     toast.toastSuccess(<HighlightWord message={tForm('toast.delete_success', { email: user.email })} />)
  //     refetch()
  //   } catch (error) {
  //     console.error(error)
  //     toast.toastFailed(tForm('toast.delete_failed'))
  //   } finally {
  //     setIsLoading(false)
  //     setUserSelected(null)
  //   }
  // }

  const isHasFilter = !!searchTerm || status !== UserOrganizationStatus.ALL

  const handleTabChange = (value: string) => {
    setTab(value)
    setSaleModeFilter('all')
    if (type === "pending") {
      navigate({ to: '/admin/outstanding', search: { tab: value, q: searchTerm || undefined } })
    } else {
      navigate({ to: '/admin/bill', search: { tab: value } })
    }
  }

  useEffect(() => {
    if (type !== 'pending') return
    if ((searchParams.tab || 'buy') === tab && (searchParams.q || '') === searchTerm) return

    navigate({
      to: '/admin/outstanding',
      search: { tab, q: searchTerm || undefined },
      replace: true,
    })
  }, [navigate, searchParams.q, searchParams.tab, searchTerm, tab, type])

  const { checkPermission } = useProfileData()
  const isBillListLoading = isPending || isPendingTotalReport

  const customerExportOptions = useMemo(
    () =>
      (customerListNoPage?.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        phone_number: c.phone_number ?? '',
        car_registration: c.car_registration ?? '',
      })),
    [customerListNoPage?.data],
  )

  const customerNameSuggestions = useMemo(
    () =>
      (customerListNoPage?.data ?? [])
        .map((c) => c.name)
        .filter((name): name is string => Boolean(name?.trim())),
    [customerListNoPage?.data],
  )

  const handleExportCsv = async (
    template: BillExportTemplate,
    customerId?: string,
  ) => {
    if (isExportingCsv) return

    const validationError = validateExportTemplate(template, tab, customerId)
    if (validationError) {
      toast.toastFailed(t(validationError))
      return
    }

    setIsExportingCsv(true)
    try {
      const catalog = await fetchExportCatalog()
      const templateSaleMode = saleModeForExportTemplate(template)
      const { rows, truncated } = await fetchAllBillsForExport({
        search: searchTerms,
        status: tab,
        sort: state.sorting,
        bill_date_from: billDateFrom || undefined,
        bill_date_to: billDateTo || undefined,
        location: locationFilter || undefined,
        payment_method: paymentMethod === 'all' ? undefined : paymentMethod,
        payment_status:
          type === 'pending' ? undefined : paymentStatus === 'all' ? undefined : paymentStatus,
        outstanding: type === 'pending',
        print_status: printStatus === 'all' ? undefined : printStatus,
        sale_mode:
          templateSaleMode ??
          (tab === 'sell' && saleModeFilter !== 'all' ? saleModeFilter : undefined),
      })
      const exportRows = filterRowsForExportTemplate(rows, {
        template,
        customerId,
        customers: customerExportOptions,
      })
      if (!hasExportableRows(exportRows, template)) {
        toast.toastFailed(t('export_csv_empty'))
        return
      }
      const csv = buildExportCsvContent(exportRows, { template, catalog })
      const stamp = dayjs().format('YYYY-MM-DD_HHmm')
      const filename = exportFilenameForTemplate(template, stamp, type === 'pending')
      downloadCsvFile(filename, csv)
      toast.toastSuccess(truncated ? t('export_csv_success_truncated') : t('export_csv_success'))
    } catch (e) {
      console.error(e)
      toast.toastFailed(t('export_csv_error'))
    } finally {
      setIsExportingCsv(false)
    }
  }

  const handleExportPdf = async (
    template: BillExportTemplate,
    year: number,
    pdfMode: PdfMode,
    month?: number,
  ) => {
    if (isExportingCsv) return
    setIsExportingCsv(true)
    try {
      await exportBillsYearPdf({
        year,
        bill_type: tab as 'buy' | 'sell',
        template,
        pdf_mode: pdfMode,
        month,
        location: locationFilter || undefined,
      })
      toast.toastSuccess(t('export_pdf_success'))
    } catch (e: any) {
      console.error(e)
      if (e?.response?.status === 404) {
        toast.toastFailed(t('export_pdf_empty'))
      } else {
        toast.toastFailed(t('export_pdf_error'))
      }
    } finally {
      setIsExportingCsv(false)
    }
  }

  return (
    <Fragment>
      <section className="relative mb-6 max-w-full overflow-x-hidden rounded-xl border border-border/90 bg-card p-4 shadow-sm md:p-5">
        <div className="mb-5 flex flex-col gap-3 border-b border-border pb-4 md:flex-row md:items-center md:justify-between md:gap-4">
          <p className="order-2 text-sm text-muted-foreground md:order-1 md:max-w-[min(100%,28rem)]">
            {type === 'pending' ? t('filter_panel_lead_outstanding') : t('filter_panel_lead')}
          </p>
          <div className="order-1 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end md:order-2">
            {checkPermission('admin_bill') ? (
              <ExportCsvDialog
                tab={tab}
                disabled={isBillListLoading}
                isExporting={isExportingCsv}
                customers={customerExportOptions}
                onExport={(exportTemplate, customerId) =>
                  void handleExportCsv(exportTemplate, customerId)
                }
                onExportPdf={(exportTemplate, year, pdfMode, month) =>
                  void handleExportPdf(exportTemplate, year, pdfMode, month)
                }
              />
            ) : null}
            {checkPermission('create_bill') ? (
              <Button
                className="w-full rounded-lg sm:w-auto"
                onClick={() => {
                  navigate({ to: '/admin/bill/create', search: { tab } })
                }}
              >
                {t('btn_create')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
          <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">ประเภท</span>
            <TabsSelect
              value={tab}
              onChange={handleTabChange}
              color={tab === 'buy' ? 'bg-red-500' : 'bg-s-brand-secondary'}
              options={[
                { name: 'ซื้อ', value: 'buy' },
                { name: 'ขาย', value: 'sell' },
              ]}
            />
          </div>
          {tab === 'sell' ? (
            <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                ช่องทางขาย
              </span>
              <Select
                value={saleModeFilter}
                onValueChange={(v) => setSaleModeFilter(v as 'all' | 'retail' | 'wholesale')}
              >
                <SelectTrigger className="min-h-11 w-full">
                  <SelectValue placeholder="ทั้งหมด" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  <SelectItem value="retail">ปลีก</SelectItem>
                  <SelectItem value="wholesale">ส่ง</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2 lg:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">ค้นหา</span>
            <BillCustomerTextCombobox
              className="min-h-11 w-full"
              placeholder="ค้นหา ชื่อลูกค้า เบอร์ เลขที่บิล…"
              disabled={isLoading}
              value={searchTerm}
              onChange={setSearchTerm}
              suggestions={customerNameSuggestions}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2 lg:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">ช่วงวันที่บิล</span>
            <DateRangePicker
              className="w-full rounded-xl border-slate-200"
              value={billDateRangeValue}
              onChange={onBillDateRangeChange}
              clearable
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">สถานที่</span>
            <Select
              value={locationFilter || 'all'}
              onValueChange={(v) => setLocationFilter(v === 'all' ? '' : (v as BillLocationValue))}
            >
              <SelectTrigger className="min-h-11 w-full">
                <SelectValue placeholder="ทุกสาขา" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกสาขา</SelectItem>
                {BILL_LOCATION_VALUES.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {BILL_LOCATION_LABELS[loc]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">ชำระโดย</span>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className="min-h-11 w-full">
                <SelectValue placeholder="ทั้งหมด" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="cash">เงินสด</SelectItem>
                <SelectItem value="transfer">เงินโอน</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type !== 'pending' ? (
            <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">สถานะการชำระ</span>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger className="min-h-11 w-full">
                  <SelectValue placeholder="ทั้งหมด" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  <SelectItem value="pending">รอการชำระ</SelectItem>
                  <SelectItem value="partial">ชำระบางส่วน</SelectItem>
                  <SelectItem value="paid">ชำระแล้ว</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">สถานะพิมพ์</span>
            <Select value={printStatus} onValueChange={setPrintStatus}>
              <SelectTrigger className="min-h-11 w-full">
                <SelectValue placeholder="ทั้งหมด" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="printed">พิมพ์แล้ว</SelectItem>
                <SelectItem value="not_printed">ยังไม่พิมพ์</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>
      <div className="relative mt-4 max-w-full px-4 md:px-8">
          <BoneyardSkeleton
            name="admin-bill-list-table"
            loading={isBillListLoading}
            fallback={(
              <div className="space-y-4 py-2">
                <UiSkeleton className="h-6 w-80 rounded-lg" />
                <UiSkeleton className="h-[420px] w-full rounded-2xl" />
              </div>
            )}
          >
            <DataTable
              {...dataTableProps}
              isFilter={isHasFilter}
              isLoading={isBillListLoading}
              totalReport={checkPermission("admin_view_calculate_bill") && <div className="flex flex-wrap gap-2">
                <div className="text-sm font-medium">จำนวนบิล: {formatNumber(totalReport?.data?.total_bill ?? 0)} บิล</div>
                <div className="text-sm font-medium">น้ำหนักรวม: {formatNumber(totalReport?.data?.total_quantity ?? 0)} กิโลกรัม</div>
                <div className="text-sm font-medium">ยอดเงิน: {formatNumber(totalReport?.data?.total_price ?? 0)} บาท</div>
              </div>}
            />
          </BoneyardSkeleton>
      </div>

      <ConfirmDeleteModal
        onConfirm={async () => {
          if (billToDelete) {
            await $apiDeleteBill.mutateAsync({ id: billToDelete.id })
            toast.toastSuccess(`ลบบิล ${billToDelete.bill_number} สำเร็จ`)
            setBillToDelete(null)
          }
        }}
        title={`ลบบิล ${billToDelete?.bill_number}`}
        description={`คุณต้องการลบบิล ${billToDelete?.bill_number} ของ ${billToDelete?.customer_information?.name} หรือไม่`}
      />
      {isLoading && <Loader.FullScreen />}
    </Fragment >
  )
}
